import { useMemo } from "react";
import type { DocumentTemplate, DocumentTemplateId } from "../constants/documentTemplates";
import { TEMPLATE_FORMS } from "../constants/templateForms";
import type { SavedFormsState } from "../components/templateForm/SavedFormPicker";
import { useSavedTemplateForms, type SavedTemplateForm } from "./useSavedTemplateForms";

/** Only the covered-works act is an import source today; every importable
 *  field in the catalogue names it. Kept as a constant rather than derived at
 *  runtime because a hook cannot be called in a loop over the schema. */
const IMPORT_SOURCE: DocumentTemplateId = "coveredWorksAct";

function stateOf(
  query: {
    data?: SavedTemplateForm[];
    isError: boolean;
    refetch: () => unknown;
  },
  projectId: string | null
): SavedFormsState {
  // With no project the query is disabled, which also reads as "no data, no
  // error" — without this the picker would say "loading" forever.
  const status: SavedFormsState["status"] =
    projectId === null ? "noProject" : query.data ? "ready" : query.isError ? "error" : "loading";
  return { list: query.data ?? [], status, retry: () => void query.refetch() };
}

/**
 * The already-filed forms a template form can start from: its own, for
 * "Choose pre-filled form", and the ones its importable fields name as their
 * source.
 *
 * Both are only fetched once a form is open, so opening the picker costs
 * nothing until a template is actually chosen.
 */
export function useTemplateFormSources(
  projectId: string | null,
  template: DocumentTemplate | null
) {
  const templateId = template?.id ?? IMPORT_SOURCE;
  const own = useSavedTemplateForms(projectId, templateId, template !== null);
  // Every importable field in the catalogue points at the same source; when
  // that is also the open template, the one query serves both.
  const source = useSavedTemplateForms(
    projectId,
    IMPORT_SOURCE,
    template !== null && templateId !== IMPORT_SOURCE
  );

  const savedForms = stateOf(own, projectId);
  const sourceState = templateId === IMPORT_SOURCE ? savedForms : stateOf(source, projectId);

  const importSources = useMemo(
    () =>
      ({ [IMPORT_SOURCE]: sourceState }) as Partial<Record<DocumentTemplateId, SavedFormsState>>,
    [sourceState]
  );

  // Sanity: a schema that named a different source would silently lose its
  // Import button, so fail loudly in development instead.
  if (import.meta.env.DEV) {
    for (const form of Object.values(TEMPLATE_FORMS)) {
      for (const section of form.sections) {
        for (const field of section.fields) {
          if (field.importFrom && field.importFrom.templateId !== IMPORT_SOURCE) {
            console.error(
              `[templateForm] ${field.id} imports from ${field.importFrom.templateId}, which useTemplateFormSources does not fetch.`
            );
          }
        }
      }
    }
  }

  return { savedForms, importSources };
}
